import React, { useEffect, useRef, useState } from "react";
import { Drawer, message } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { saveCategories, CATEGORY_COLORS, MAX_CATEGORIES, type Category } from "../store/settingsSlice";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { Button } from "./ui";

interface CategoryDrawerProps {
  open: boolean;
  /** "add" = create new, "edit" = edit existing */
  mode: "add" | "edit";
  /** pre-fill when mode === "edit" */
  initialName?: string;
  initialColor?: string;
  onClose: () => void;
  onSaved?: (savedName: string) => void;
}

export function CategoryDrawer({
  open,
  mode,
  initialName = "",
  initialColor = CATEGORY_COLORS[0],
  onClose,
  onSaved,
}: CategoryDrawerProps) {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { categories, saving } = useAppSelector((s) => s.settings);
  const getToken = () => getAccessTokenSilently({ cacheMode: "on" });

  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);
  const inputRef = useRef<HTMLInputElement>(null);
  const dreamCards = useAppSelector((s) => (s as any).dashboard?.dreamCards ?? []);

  /* reset fields when drawer opens */
  useEffect(() => {
    if (open) {
      setName(initialName);
      setColor(initialColor);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, initialName, initialColor]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    let next: Category[];

    if (mode === "add") {
      if (categories.length >= MAX_CATEGORIES) {
        message.info(`Maximum ${MAX_CATEGORIES} categories allowed`);
        return;
      }
      if (categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
        message.info("A category with that name already exists");
        return;
      }
      next = [...categories, { name: trimmed, color }];
    } else {
      /* edit mode — replace the matching category by initialName */
      const isDuplicate = categories.some(
        (c) => c.name.toLowerCase() === trimmed.toLowerCase() && c.name !== initialName
      );
      if (isDuplicate) {
        message.info("A category with that name already exists");
        return;
      }
      next = categories.map((c) =>
        c.name === initialName ? { name: trimmed, color } : c
      );
    }

    const action = await dispatch(saveCategories({ categories: next, getAccessToken: getToken }));
    if (saveCategories.fulfilled.match(action)) {
      message.success(mode === "add" ? "Category added" : "Category updated");

      /* In edit mode, cascade color/name changes to all dreams in this category */
      if (mode === "edit") {
        const nameChanged = trimmed !== initialName;
        const colorChanged = color !== initialColor;
        if (nameChanged || colorChanged) {
          const affected = dreamCards.filter((d: any) => d.category === initialName);
          if (affected.length > 0) {
            const patch: Record<string, string> = {};
            if (nameChanged) patch.category = trimmed;
            if (colorChanged) patch.color = color;
            Promise.all(
              affected.map((d: any) =>
                request(API_URLS.dream(d.id), {
                  method: "PUT",
                  auth: true,
                  getAccessToken: getToken,
                  body: patch,
                }).catch(() => {})
              )
            );
          }
        }
      }

      onSaved?.(trimmed);
      onClose();
    } else {
      message.error("Failed to save category");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add category" : "Edit category"}
      width={360}
      destroyOnClose
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" size="md" block onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            block
            loading={saving}
            disabled={!name.trim()}
            onClick={handleSave}
          >
            {mode === "add" ? "Add category" : "Save changes"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Name input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-slate-700">Category name</label>
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            placeholder="e.g. Health, Travel, Career"
            maxLength={30}
            style={{
              width: "100%",
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 14,
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#0088FF"; }}
            onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }}
          />
        </div>

        {/* Color picker */}
        <div className="flex flex-col gap-3">
          <label className="text-sm font-semibold text-slate-700">Color</label>
          <div className="grid grid-cols-6 gap-2.5">
            {CATEGORY_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 36, height: 36,
                  borderRadius: "50%",
                  background: c,
                  border: color === c ? "3px solid #0b1b3a" : "2px solid transparent",
                  boxShadow: color === c ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "transform 0.1s, box-shadow 0.1s",
                  transform: color === c ? "scale(1.15)" : "scale(1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {color === c && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        {name.trim() && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-700">Preview</label>
            <div
              className="flex items-center justify-between rounded-xl px-4 py-3"
              style={{ background: color }}
            >
              <span className="text-sm font-bold uppercase tracking-wide text-white">
                {name.trim()}
              </span>
              <span className="rounded-full bg-white/25 px-2 py-0.5 text-xs font-bold text-white">
                0
              </span>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
